import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Eye, EyeOff, AlertCircle, Loader2, Mail } from "lucide-react";
import { toast } from "react-toastify";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { GLOBAL_IMAGES } from "../assets/asset";
import { useMutation } from "@tanstack/react-query";
import useResponsiveGoogleWidth from "../hooks/useResponsiveGoogleWidth";
import {
  startJourney,
  logSignupSubmitted,
  logSignupApiResult,
  logEmailVerificationSent,
} from "../utils/datadogLogger";
import {
  extractStudentEmail,
  extractStudentJwtToken,
  extractStudentPreAuthToken,
  extractStudentUserRecord,
  isAlreadyVerifiedResponse,
  isPendingStudentVerificationResponse,
  studentAuthApi,
} from "../features/auth/authApi";
import { createStudentVerificationFlow } from "../features/auth/studentOtpFlow";
import { finalizeStudentLogin } from "../features/auth/useLogin";

const GENERIC_SIGNUP_ERROR = "Unable to complete the request right now.";
const GENERIC_VERIFICATION_SENT_MESSAGE = "Verification code sent to your email.";
const GENERIC_ALREADY_VERIFIED_MESSAGE = "This account is already verified.";
const GENERIC_GOOGLE_SIGNUP_ERROR = "Unable to sign up with Google. Please try again.";

const CreateAccountForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const signupMutation = useMutation({
    mutationKey: ["signup"],
    mutationFn: (data) => studentAuthApi.signupV2(data),
  });

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVerificationAlert, setShowVerificationAlert] = useState(false);
  const [googleError, setGoogleError] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { containerRef: googleButtonRef, buttonWidth: googleButtonWidth } =
    useResponsiveGoogleWidth();

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    const hasMinLength = password.length >= 8;
    const hasLetter = /[a-zA-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: hasMinLength && hasLetter && hasNumber && hasSpecialChar,
      requirements: {
        minLength: hasMinLength,
        hasLetter,
        hasNumber,
        hasSpecialChar,
      },
    };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleResendVerification = () => {
    studentAuthApi
      .resendSignupOtp({ email: formData.email.trim() })
      .then((response) => {
        const nextContext = createStudentVerificationFlow({
          email: extractStudentEmail(response) || formData.email,
          preAuthToken: extractStudentPreAuthToken(response),
          verificationType: response?.data?.data?.verification_type || "otp",
          source: "signup",
        });

        navigate("/verification", {
          state: { email: nextContext.email, source: "signup" },
        });
      })
      .catch((error) => {
        setErrors({
          submit: GENERIC_SIGNUP_ERROR,
        });
      });
  };

  const handleSignup = async () => {

    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    const passwordValidation = validatePassword(formData.password);
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!passwordValidation.isValid) {
      newErrors.password = "Password must meet all requirements";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Log signup submitted
    logSignupSubmitted(formData.email, 'web');

    const payload = {
      email: formData.email.trim().toLowerCase(),
      password: formData.password,
    };

    signupMutation.mutate(payload, {
      onSuccess: async (data) => {
        console.log("suuccccess");
        // Log signup API success
        logSignupApiResult(
          formData.email,
          data?.status || 200,
          { email: formData.email }, // Masked request body
          { success: true } // Masked response body
        );

        // Log email verification sent
        logEmailVerificationSent(formData.email);

        const nextContext = createStudentVerificationFlow({
          email: extractStudentEmail(data) || payload.email,
          preAuthToken: extractStudentPreAuthToken(data),
          verificationType: data?.data?.data?.verification_type || "otp",
          accountType: data?.data?.data?.account_type || "student",
          source: "signup",
        });

        navigate("/verification", {
          state: {
            email: nextContext.email,
            source: "signup",
            successMessage: GENERIC_VERIFICATION_SENT_MESSAGE,
          },
        });
      },
      onError: async (error) => {
        const errorData = error?.response?.data || {};
        const errorMessage = (errorData.message || "").trim();
        const statusCode = String(errorData.status_code || error?.response?.status || "");

        // Log signup API error
        logSignupApiResult(
          formData.email,
          error?.response?.status || 500,
          { email: formData.email }, // Masked request body
          errorData, // Response body
          errorMessage
        );

        if (isAlreadyVerifiedResponse(errorData)) {
          setErrors({
            submit: GENERIC_ALREADY_VERIFIED_MESSAGE,
          });
          return;
        }

        if (isPendingStudentVerificationResponse(errorData)) {
          try {
            const resendResponse = await studentAuthApi.resendSignupOtp({
              email: payload.email,
            });
            const nextContext = createStudentVerificationFlow({
              email: extractStudentEmail(resendResponse) || payload.email,
              preAuthToken: extractStudentPreAuthToken(resendResponse),
              verificationType:
                resendResponse?.data?.data?.verification_type || "otp",
              source: "signup",
            });

            navigate("/verification", {
              state: {
                email: nextContext.email,
                source: "signup",
                successMessage: GENERIC_VERIFICATION_SENT_MESSAGE,
              },
            });
          } catch (resendError) {
            const resendErrorData = resendError?.response?.data || {};

            if (isAlreadyVerifiedResponse(resendErrorData)) {
              setErrors({
                submit: GENERIC_ALREADY_VERIFIED_MESSAGE,
              });
              return;
            }

            createStudentVerificationFlow(
              {
                email: payload.email,
                source: "signup",
                expiresAt: Date.now(),
              },
              { resetCycle: false }
            );
            setShowVerificationAlert(true);
            setErrors({
              submit: GENERIC_SIGNUP_ERROR,
            });
          }
        } else {
          const submitMessage =
            errorMessage === "User Already Exist"
              ? "User with this e-mail already exists."
              : GENERIC_SIGNUP_ERROR;

          setErrors({
            submit: submitMessage,
          });
        }
      },
    });
  };

  return (
    <div
      className="signup-page min-h-screen flex flex-col items-center justify-center font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="signup-page-logo absolute top-6 left-6">
        <img
          src={GLOBAL_IMAGES.dingo}
          alt="Mathdingo Logo"
          className="signup-page-logo-img w-20 h-20"
        />
      </div>

      <div className="signup-page-wrap w-full max-w-xl px-4">
        {showVerificationAlert ? (
          <div className="signup-page-card signup-page-verification bg-white/60 rounded-3xl p-8 shadow-lg">
            <div className="signup-page-verification-content text-center">
              <div className="signup-page-mail-icon flex justify-center mb-4">
                <Mail size={64} className="signup-page-mail-svg text-blue-600" />
              </div>
              <h2 className="signup-page-verification-title text-3xl font-bold mb-4">Verification Required</h2>
              <p className="signup-page-verification-copy text-lg mb-6">
                We've already sent a verification email to <strong>{formData.email}</strong>. 
                Please check your inbox and verify your account before logging in.
              </p>
              <p className="signup-page-verification-copy text-md mb-8">
                If you can't find the email, check your spam folder or click the button below to resend.
              </p>
              <div className="signup-page-verification-actions flex flex-col gap-4">
                <button
                  onClick={handleResendVerification}
                  className="signup-page-submit w-full bg-blue-800 text-white py-3 rounded-full hover:bg-blue-900 transition-colors text-xl font-semibold"
                >
                  Resend Verification Email
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="signup-page-login-link text-blue-800 text-xl font-semibold hover:underline"
                >
                  Back to Login
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="signup-page-card bg-white/60 rounded-3xl p-8 shadow-lg">
            <div className="signup-page-header flex items-center gap-4 mb-8">
              <img
                src={GLOBAL_IMAGES.LoginBackButton}
                alt="Back"
                className="signup-page-back-img w-14 h-14 cursor-pointer"
                onClick={() => navigate(-1)}
              />
              <h2 className="signup-page-title text-4xl font-bold">Create your account</h2>
            </div>

            <div className="signup-page-form space-y-6 w-full max-w-[400px] mx-auto">
              <div className="signup-page-field">
                <label className="signup-page-label block text-xl font-bold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  autoComplete="off"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="signup-page-input w-full px-4 py-3 rounded-full border-2 border-gray-300 focus:outline-none focus:border-blue-500"
                  placeholder="Email address"
                />
                {errors.email && (
                  <div className="signup-page-error flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                    <AlertCircle size={16} />
                    <span>{errors.email}</span>
                  </div>
                )}
              </div>

              <div className="signup-page-field">
                <label className="signup-page-label block text-xl font-bold text-gray-700 mb-2">
                  Password
                </label>
                <div className="signup-page-password-wrap relative">
                  <input
                    autoComplete="off"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`signup-page-input w-full px-4 py-3 rounded-full border-2 ${
                      errors.password ? "border-red-300" : "border-gray-300"
                    } pr-12 focus:outline-none focus:border-blue-500`}
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="signup-page-password-toggle absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={24} /> : <Eye size={24} />}
                  </button>
                </div>
                {errors.password && (
                  <div className="signup-page-error flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                    <AlertCircle size={16} />
                    <span>
                      Minimum 8 characters, with letters, numbers and special
                      characters
                    </span>
                  </div>
                )}
              </div>

              <div className="signup-page-field">
                <label className="signup-page-label block text-xl font-bold text-gray-700 mb-2">
                  Re-enter Password
                </label>
                <div className="signup-page-password-wrap relative">
                  <input
                    autoComplete="off"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`signup-page-input w-full px-4 py-3 rounded-full border-2 ${
                      errors.confirmPassword
                        ? "border-red-300"
                        : "border-gray-300"
                    } pr-12 focus:outline-none focus:border-blue-500`}
                    placeholder="Re-enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="signup-page-password-toggle absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={24} />
                    ) : (
                      <Eye size={24} />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <div className="signup-page-error flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                    <AlertCircle size={16} />
                    <span>{errors.confirmPassword}</span>
                  </div>
                )}
              </div>

              {errors.submit && (
                <div className="signup-page-error signup-page-submit-error flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 rounded-full text-red-700 text-sm">
                  <AlertCircle size={16} />
                  <span>{errors.submit}</span>
                </div>
              )}

              <button
                onClick={handleSignup}
                disabled={signupMutation.isPending}
                className="signup-page-submit w-full bg-blue-800 text-white py-3 rounded-full hover:bg-blue-900 transition-colors text-xl font-semibold flex items-center justify-center"
              >
                {signupMutation.isPending ? (
                  <Loader2 className="animate-spin" size={24} />
                ) : (
                  "Signup"
                )}
              </button>

              <div className="signup-page-login text-center mt-4">
                <p className="signup-page-login-copy text-xl text-gray-700 mb-2">Already registered?</p>
                <button
                  onClick={() => navigate("/login")}
                  className="signup-page-login-link text-blue-800 text-xl font-semibold hover:underline"
                >
                  Login
                </button>
              </div>

              {/* Google Sign Up divider and button */}
              <div className="signup-page-or-divider flex items-center gap-3 mt-6">
                <div className="flex-1 border-t border-gray-400"></div>
                <span className="text-gray-500 text-sm font-semibold">or sign up with</span>
                <div className="flex-1 border-t border-gray-400"></div>
              </div>

              <div className="signup-page-google auth-google-shell mt-4 flex flex-col items-center">
                {isGoogleLoading ? (
                  <button
                    disabled
                    className="w-full bg-white text-gray-700 border-2 border-gray-300 py-3 rounded-full flex items-center justify-center opacity-60 cursor-not-allowed text-xl font-semibold"
                  >
                    <Loader2 className="animate-spin mr-2" size={22} />
                    Signing up...
                  </button>
                ) : (
                  <div
                    ref={googleButtonRef}
                    className="auth-google-button-wrap flex min-h-[44px] w-full justify-center"
                  >
                    <GoogleLogin
                      onSuccess={async (credentialResponse) => {
                        setGoogleError("");
                        setIsGoogleLoading(true);
                        try {
                          const idToken = credentialResponse.credential;
                          if (!idToken) {
                            setGoogleError(GENERIC_GOOGLE_SIGNUP_ERROR);
                            setIsGoogleLoading(false);
                            return;
                          }
                          let username = "";
                          let email = "";
                          try {
                            const decoded = jwtDecode(idToken);
                            email = decoded.email || "";
                            username = decoded.name || email.split("@")[0] || "";
                          } catch (_e) {
                            username = "";
                            email = "";
                          }
                          const response = await studentAuthApi.googleSignup({ id_token: idToken, username, email });
                          const profileData = extractStudentUserRecord(response, {
                            jwtoken: extractStudentJwtToken(response),
                          });
                          if (!profileData.jwtoken) {
                            setGoogleError(GENERIC_GOOGLE_SIGNUP_ERROR);
                            setIsGoogleLoading(false);
                            return;
                          }
                          finalizeStudentLogin({
                            dispatch,
                            navigate,
                            profileData,
                            shouldShowFirstSignupDemo: true,
                          });
                        } catch (error) {
                          const errMsg = error?.response?.data?.message || GENERIC_GOOGLE_SIGNUP_ERROR;
                          setGoogleError(errMsg);
                          toast.error(errMsg, {
                            ariaLabel: "Google Signup Error",
                            autoClose: 2500,
                            theme: "colored",
                            hideProgressBar: true,
                            className: "font-mali",
                          });
                        } finally {
                          setIsGoogleLoading(false);
                        }
                      }}
                      onError={() => setGoogleError(GENERIC_GOOGLE_SIGNUP_ERROR)}
                      text="signup_with"
                      shape="rectangular"
                      size="large"
                      width={googleButtonWidth}
                      logo_alignment="left"
                    />
                  </div>
                )}
              </div>

              {googleError && (
                <div className="signup-page-google-error flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                  <AlertCircle size={16} />
                  <span>{googleError}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateAccountForm;
